import { useLayoutEffect, useRef, useState } from "react";

import styles from "../opsDashboard.module.css";
import { Formula } from "./Formula";

/**
 * The info-circle beside a table title, with the mockup's hover/focus tooltip.
 *
 * Carries what the table shows (the mockup's own blurb), the rule typeset as maths, and
 * the one caveat the maths cannot express. Ops asked for the calculation directly — "how
 * is this value calculated" should be answerable without asking us — so it travels from
 * the rule that performs it, interpolating the live threshold constants, rather than
 * being written into the page where it would drift the first time a number is tuned.
 */
const TIER_DOT: Record<string, string> = {
    danger: styles.tipDotDanger,
    warning: styles.tipDotWarning,
    success: styles.tipDotSuccess,
    neutral: styles.tipDotNeutral,
};

export function InfoTip({
    text, formula, method, tiers,
}: {
    text?: string;
    formula?: string;
    method?: string;
    /** [tone, condition] pairs, worst first. Empty where the table has no agreed tiers. */
    tiers?: string[][];
}) {
    // Governed by the DESCRIPTION, not the calculation. Every table has a calculation
    // now, so keying off that put an icon on cards the design leaves bare.
    //
    // A COLOUR LEGEND ALSO EARNS ONE. A badge nobody can decode is the case this icon
    // exists for, and List Size and Test File Received both colour a column while the
    // design gives their card no description — leaving the thresholds with nowhere to
    // be read.
    const calculation = formula || method;

    const [open, setOpen] = useState(false);
    // Margin By Campaign is the last card on the page, so its tooltip opened into the
    // gap below the footer and lost its last few lines. Measured rather than assumed:
    // the bubble is tall and the card it hangs off can sit anywhere in the scroll.
    const [up, setUp] = useState(false);
    const body = useRef<HTMLSpanElement>(null);

    useLayoutEffect(() => {
        const el = body.current;
        if (!open || !el) return;
        const anchor = el.parentElement!.getBoundingClientRect();
        const needed = el.offsetHeight + 8;
        // Flipping is only an improvement when there is actually room above.
        setUp(window.innerHeight - anchor.bottom < needed && anchor.top > needed);
    }, [open]);

    if (!text && !tiers?.length) return null;

    return (
        <span
            className={styles.tip}
            tabIndex={0}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
        >
            <i className="bi bi-info-circle" style={{ fontSize: "12.5px", lineHeight: 1 }} />
            <span
                ref={body}
                className={`${styles.tipBody}${open ? ` ${styles.tipOpen}` : ""}${
                    up ? ` ${styles.tipUp}` : ""
                }`}
            >
                {text}
                {calculation && (
                    <>
                        {!!text && <span className={styles.tipRule} />}
                        <span className={styles.tipLabel}>How this is calculated</span>
                        {formula && <Formula source={formula} />}
                        {method && (
                            <span className={formula ? styles.tipCaveat : undefined}>
                                {method}
                            </span>
                        )}
                    </>
                )}
                {!!tiers?.length && (
                    <>
                        {(text || calculation) && <span className={styles.tipRule} />}
                        <span className={styles.tipLabel}>What the colours mean</span>
                        <span className={styles.tipTiers}>
                            {tiers.map(([tone, condition]) => (
                                <span key={tone} className={styles.tipTier}>
                                    <span
                                        className={`${styles.tipDot} ${
                                            TIER_DOT[tone] ?? styles.tipDotNeutral
                                        }`}
                                    />
                                    <span>{condition}</span>
                                </span>
                            ))}
                        </span>
                    </>
                )}
            </span>
        </span>
    );
}
